<script setup>
import {
  ref,
  watch,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from "vue";
import { useStore } from "vuex";
import MainButton from "../inputs/MainButton.vue";
import DescriptionText from "./DescriptionText.vue";
import InputBox from "../inputs/InputBox.vue";
import Typewriter from "typewriter-effect/dist/core";
import { nextStep } from "@/utils/shepherd/main";
import { pickExamples } from "@/utils/examples";

const store = useStore();

const prompt = computed(() => store.state.prompt);
const error = computed(() => store.state.promptError);

const changePrompt = (prompt) => store.dispatch("changePrompt", { prompt });
const setNewInput = (state) => store.dispatch("setNewInput", state);

const promptInput = ref(prompt.value);
const inputBox = ref(null);
var placeholder = ref("Whatever you can think of...");

// Typewriter effects
const customNodeCreator = function (character) {
  placeholder.value = placeholder.value + character;
  return null;
};

const onRemoveNode = function () {
  if (placeholder.value) {
    placeholder.value = placeholder.value.slice(0, -1);
  }
};

const typewriter = new Typewriter(null, {
  onCreateTextNode: customNodeCreator,
  onRemoveNode: onRemoveNode,
});

const startTypewriter = () => {
  placeholder.value = "";
  typewriter
    .typeString("Bedtime story for my children")
    .pauseFor(1000)
    .deleteAll(15)
    .typeString("List of dinner ideas")
    .pauseFor(1000)
    .deleteAll(15)
    .typeString("Response to my boss")
    .pauseFor(1000)
    .deleteAll(15)
    .typeString("Whatever you can think of...")
    .start();
};

onMounted(() => {
  window.addEventListener("start-typewriter", startTypewriter);
});

onBeforeUnmount(() => {
  window.removeEventListener("start-typewriter", startTypewriter);
});

// Toggle if the user is asking for a new prompt or not
watch(promptInput, () => {
  if (
    prompt.value.toLowerCase() === promptInput.value.toLowerCase() ||
    promptInput.value.length === 0
  ) {
    setNewInput(false);
  } else {
    setNewInput(true);
  }
});

// If re-loaded and prompt takes a second to load, show the prompt in the inputBox
watch(prompt, (newVal, oldVal) => {
  if (oldVal === "" && promptInput.value !== prompt.value) {
    promptInput.value = prompt.value;
  }
  if (oldVal === promptInput.value) {
    promptInput.value = prompt.value;
  }
});

const blurInput = () => {
  if (inputBox.value) {
    inputBox.value?.blur();
  }
};

const initialCheck = () => {
  nextStep("typing-complete");
  blurInput();
  changePrompt(promptInput.value);
};

// One tap starts a make from an example, for anyone unsure what to ask for: a few
// picked at random from many, fresh each time the start page shows
const examples = pickExamples(3);
// If three don't fit on one row, two are shown. The row never wraps, so it stays the
// same height however the text changes (the page's font arriving late, say) and
// nothing above it jumps.
const exampleRow = ref(null);
const tryLabel = ref(null);
const exampleCount = ref(examples.length);
const fitExamples = async () => {
  exampleCount.value = examples.length;
  await nextTick();
  const row = exampleRow.value;
  if (row && row.scrollWidth > row.clientWidth + 1) exampleCount.value = 2;
};
// "Try" only changes size with the font or the screen, so watching it catches both
// without reacting to the chips it adds or removes itself
let labelObserver;
onMounted(() => {
  fitExamples();
  window.addEventListener("resize", fitExamples);
  document.fonts?.ready.then(fitExamples);
  if (window.ResizeObserver && tryLabel.value) {
    labelObserver = new ResizeObserver(() => fitExamples());
    labelObserver.observe(tryLabel.value);
  }
});
onBeforeUnmount(() => {
  window.removeEventListener("resize", fitExamples);
  labelObserver?.disconnect();
});
const startExample = (example) => {
  promptInput.value = example.prompt;
  initialCheck();
};
</script>

<template>
  <section
    id="shepherd-target-startbar"
    class="w-full mt-5 sm:mt-6 grid lg:grid-cols-3 lg:gap-11 grid-rows-1 lg:grid-rows-none"
  >
    <InputBox
      ref="inputBox"
      id="inputBox"
      :placeholder="placeholder"
      class="py-4 sm:py-5 lg:col-span-2 lg:px-4"
      defaultClasses="text-gray-10 bg-primary text-xl border-2 px-4"
      :error="error"
      v-model="promptInput"
      @submit="initialCheck()"
    />
    <MainButton
      id="inputBox"
      class="mt-2 lg:mt-6 mb-8 lg:mb-8 lg:-ml-2 lg:mr-3"
      hideCorner
      @clicked="initialCheck()"
    >
      <label class="uppercase cursor-pointer">Start Make</label>
      <font-awesome-icon
        class="ml-2 cursor-pointer"
        :icon="['fas', 'chevron-right']"
      />
    </MainButton>
    <div
      ref="exampleRow"
      class="-mt-5 mb-5 flex items-center justify-center gap-2 overflow-hidden p-1 lg:hidden"
    >
      <span ref="tryLabel" class="shrink-0 text-sm text-secondary">Try</span>
      <button
        v-for="example in examples.slice(0, exampleCount)"
        :key="example.label"
        type="button"
        class="shrink-0 rounded-full border border-gray-200 px-3 py-1.5 text-sm text-primary transition-colors hover:border-accentPrimary hover:text-accentPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-accentPrimary"
        :title="example.prompt"
        @click="startExample(example)"
      >
        {{ example.label }}
      </button>
    </div>
    <DescriptionText
      class="block text-center !text-sm !text-secondary sm:!text-base lg:hidden [@media(max-width:639px)_and_(max-height:600px)]:hidden"
      location="above"
    />
  </section>
</template>
